/**
 * Leviathan Web - Statement Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Detailed view of a customer statement
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Printer,
  Mail,
  CheckCircle,
  Clock,
  Send,
  Eye,
  FileText,
  Calendar,
  User,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface Statement {
  id: string;
  statementNumber: string;
  periodStart: string;
  periodEnd: string;
  statementDate: string;
  dueDate: string | null;
  previousBalance: number;
  totalCharges: number;
  totalCredits: number;
  endingBalance: number;
  current: number;
  over30: number;
  over60: number;
  over90: number;
  status: 'DRAFT' | 'SENT' | 'VIEWED' | 'PAID';
  customer: {
    id: string;
    customerNumber: string;
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    email: string | null;
  };
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  total: number;
  status: string;
}

interface Payment {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  amount: number;
  paymentMethod: string;
}

const STATUS_CONFIG = {
  DRAFT: {
    label: 'Draft',
    icon: <Clock className="w-4 h-4" />,
    className: 'bg-panel-dark border-panel-border text-text-secondary',
  },
  SENT: {
    label: 'Sent',
    icon: <Send className="w-4 h-4" />,
    className: 'bg-steel/20 border-steel text-steel',
  },
  VIEWED: {
    label: 'Viewed',
    icon: <Eye className="w-4 h-4" />,
    className: 'bg-amber/20 border-amber text-amber',
  },
  PAID: {
    label: 'Paid',
    icon: <CheckCircle className="w-4 h-4" />,
    className: 'bg-success-green/20 border-success-green text-success-green',
  },
} as const;

/**
 * Panel component
 */
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6">
        <h3 className="text-lg font-mono text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  highlight = false,
  status,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  status?: 'success' | 'warning' | 'error';
}) {
  const textColor = status
    ? status === 'success'
      ? 'text-success-green'
      : status === 'warning'
        ? 'text-warning-amber'
        : 'text-error-red'
    : highlight
      ? 'text-amber'
      : 'text-text-primary';

  return (
    <div
      className={`relative bg-panel-dark border p-4 ${highlight ? 'border-amber' : 'border-panel-border'}`}
    >
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-2">
        {label}
      </div>
      <div className={`text-lg font-mono font-bold ${textColor}`}>{value}</div>
    </div>
  );
}

/**
 * Statement Detail Page
 */
const StatementDetail = () => {
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [statement, setStatement] = useState<Statement | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStatement();
  }, [id]);

  const fetchStatement = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/statements/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStatement(data.statement);
        setInvoices(data.invoices || []);
        setPayments(data.payments || []);
      }
    } catch (error) {
      console.error('Failed to fetch statement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (newStatus: string) => {
    try {
      const response = await fetch(`${API_URL}/api/reports/statements/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        fetchStatement();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const sendEmail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/send/statement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ statementId: id }),
      });
      if (response.ok) {
        alert('Statement email sent successfully!');
        updateStatus('SENT');
      } else {
        alert('Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send email');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">Loading statement...</div>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-error-red font-mono">Statement not found</div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[statement.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.DRAFT;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/reports/statements"
            className="p-2 text-text-tertiary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-4">
            <FileText className="w-8 h-8 text-amber" />
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
                  {statement.statementNumber}
                </h1>
                <span
                  className={`inline-flex items-center space-x-1.5 px-3 py-1 border text-sm font-mono ${statusConfig.className}`}
                >
                  {statusConfig.icon}
                  <span>{statusConfig.label}</span>
                </span>
              </div>
              <p className="text-sm text-text-secondary font-mono mt-1">
                {statement.customer.name}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Link
          to={`/reports/statements/${id}/print`}
          target="_blank"
          className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors"
        >
          <Printer className="w-4 h-4" />
          <span>Print Statement</span>
        </Link>
        {statement.customer.email && statement.status === 'DRAFT' && (
          <button
            onClick={sendEmail}
            className="flex items-center space-x-2 bg-steel text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-steel/80 transition-colors"
          >
            <Mail className="w-4 h-4" />
            <span>Email Statement</span>
          </button>
        )}
        {statement.status === 'DRAFT' && (
          <button
            onClick={() => updateStatus('SENT')}
            className="flex items-center space-x-2 border border-panel-border text-text-secondary px-4 py-2 font-mono text-sm uppercase tracking-wider hover:text-amber hover:border-amber transition-colors"
          >
            <Send className="w-4 h-4" />
            <span>Mark as Sent</span>
          </button>
        )}
        {(statement.status === 'SENT' || statement.status === 'VIEWED') && (
          <button
            onClick={() => updateStatus('PAID')}
            className="flex items-center space-x-2 border border-success-green text-success-green px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-success-green/10 transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Mark as Paid</span>
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Previous Balance" value={formatCurrency(statement.previousBalance)} />
        <StatCard
          label="Charges"
          value={formatCurrency(statement.totalCharges)}
          status="error"
        />
        <StatCard
          label="Payments"
          value={formatCurrency(statement.totalCredits)}
          status="success"
        />
        <StatCard
          label="Ending Balance"
          value={formatCurrency(statement.endingBalance)}
          highlight
        />
        <StatCard
          label="Due Date"
          value={statement.dueDate ? formatDate(statement.dueDate) : 'Upon Receipt'}
        />
      </div>

      {/* Info Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Info */}
        <Panel title="Customer">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <User className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
              <div>
                <Link
                  to={`/customers/${statement.customer.id}`}
                  className="text-amber hover:text-amber/80 font-mono font-semibold"
                >
                  {statement.customer.name}
                </Link>
                <div className="text-text-tertiary font-mono text-sm">
                  {statement.customer.customerNumber}
                </div>
              </div>
            </div>
            {statement.customer.address && (
              <div className="text-text-secondary font-mono text-sm ml-8">
                <div>{statement.customer.address}</div>
                <div>
                  {statement.customer.city}, {statement.customer.state}{' '}
                  {statement.customer.zip}
                </div>
              </div>
            )}
            {statement.customer.email && (
              <div className="text-text-secondary font-mono text-sm ml-8">
                {statement.customer.email}
              </div>
            )}
          </div>
        </Panel>

        {/* Statement Period */}
        <Panel title="Statement Period">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-text-tertiary font-mono text-sm flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Period Start</span>
              </span>
              <span className="text-text-primary font-mono">
                {formatDate(statement.periodStart)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-tertiary font-mono text-sm flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Period End</span>
              </span>
              <span className="text-text-primary font-mono">
                {formatDate(statement.periodEnd)}
              </span>
            </div>
            <div className="flex justify-between items-center border-t border-panel-border pt-3">
              <span className="text-text-tertiary font-mono text-sm">Statement Date</span>
              <span className="text-text-primary font-mono">
                {formatDate(statement.statementDate)}
              </span>
            </div>
          </div>
        </Panel>

        {/* Aging Summary */}
        <Panel title="Aging Summary">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-text-tertiary font-mono text-sm">Current</span>
              <span className="text-success-green font-mono">
                {formatCurrency(statement.current)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-tertiary font-mono text-sm">31-60 Days</span>
              <span className="text-amber font-mono">{formatCurrency(statement.over30)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-tertiary font-mono text-sm">61-90 Days</span>
              <span className="text-warning-amber font-mono">
                {formatCurrency(statement.over60)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-tertiary font-mono text-sm">90+ Days</span>
              <span className="text-error-red font-mono">
                {formatCurrency(statement.over90)}
              </span>
            </div>
          </div>
        </Panel>
      </div>

      {/* Invoices */}
      <Panel title={`Invoices (${invoices.length})`}>
        {invoices.length === 0 ? (
          <div className="text-center py-4 text-text-tertiary font-mono">
            No invoices in this period
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-panel-border">
              <tr>
                <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Invoice #
                </th>
                <th className="text-center px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Date
                </th>
                <th className="text-center px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Due Date
                </th>
                <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b border-panel-border/50 hover:bg-amber/5"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/invoices/${inv.id}`}
                      className="text-amber hover:text-amber/80 font-mono"
                    >
                      {inv.invoiceNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-center text-text-secondary font-mono text-sm">
                    {formatDate(inv.invoiceDate)}
                  </td>
                  <td className="px-4 py-3 text-center text-text-secondary font-mono text-sm">
                    {formatDate(inv.dueDate)}
                  </td>
                  <td className="px-4 py-3 text-right text-error-red font-mono">
                    {formatCurrency(inv.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-amber">
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-2 text-right text-text-secondary font-mono uppercase text-sm"
                >
                  Total Charges
                </td>
                <td className="px-4 py-2 text-right text-error-red font-mono font-bold">
                  {formatCurrency(statement.totalCharges)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </Panel>

      {/* Payments */}
      <Panel title={`Payments (${payments.length})`}>
        {payments.length === 0 ? (
          <div className="text-center py-4 text-text-tertiary font-mono">
            No payments in this period
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-panel-border">
              <tr>
                <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Payment #
                </th>
                <th className="text-center px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Date
                </th>
                <th className="text-center px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Method
                </th>
                <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {payments.map((pmt) => (
                <tr
                  key={pmt.id}
                  className="border-b border-panel-border/50 hover:bg-amber/5"
                >
                  <td className="px-4 py-3 text-steel font-mono">{pmt.paymentNumber}</td>
                  <td className="px-4 py-3 text-center text-text-secondary font-mono text-sm">
                    {formatDate(pmt.paymentDate)}
                  </td>
                  <td className="px-4 py-3 text-center text-text-secondary font-mono text-sm">
                    {pmt.paymentMethod}
                  </td>
                  <td className="px-4 py-3 text-right text-success-green font-mono">
                    ({formatCurrency(pmt.amount)})
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-amber">
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-2 text-right text-text-secondary font-mono uppercase text-sm"
                >
                  Total Payments
                </td>
                <td className="px-4 py-2 text-right text-success-green font-mono font-bold">
                  ({formatCurrency(statement.totalCredits)})
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </Panel>
    </div>
  );
};

export default StatementDetail;
